import {
  AnonymousCheckoutResponse,
  CartLineItems,
  Landmarks,
  PaymentMethod,
  StoreInterface,
} from './../interfaces/index';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import purchaseServices from '../services/purchaseService'; // Import the service
import {
  CartItem,
  PurchaseOrder,
  OrderDetails,
  Order,
  Checkout,
} from '../interfaces'; // Assuming the type is defined
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';

export interface PurchaseState {
  purchase: PurchaseOrder | null;
  miscellanousFeeName: string | null;
  purchaseCopy: PurchaseOrder | null;
  cartLineItemsCopy: CartLineItems[];
  orderDetailsCopy: OrderDetails | null;
  loading: boolean;
  paymentMethods?: PaymentMethod[] | null;
  orders: Order[] | null;
  error: string | null;
  oneTimeAccount: string | null;
  oneTimeAccountRef: string | null;
  bankName: string | null;
  checkoutResponse: AnonymousCheckoutResponse | null;
  anonymousCheckoutResponse: AnonymousCheckoutResponse | null;
  landmarks: Landmarks[];
  store: StoreInterface | null;
}

const initialState: PurchaseState = {
  purchase: null,
  purchaseCopy: null,
  miscellanousFeeName: 'Miscellaneous Fee',
  cartLineItemsCopy: [],
  orderDetailsCopy: null,
  loading: false,
  orders: null,
  error: null,
  oneTimeAccount: null,
  oneTimeAccountRef: null,
  bankName: null,
  checkoutResponse: null,
  anonymousCheckoutResponse: null,
  landmarks: [],
  store: null,
};

// Async thunk for fetching purchase cart
export const fetchPurchaseCart = createAsyncThunk(
  'purchase/getPurchaseCart',

  async (_, { rejectWithValue }) => {
    try {
      const purchaseData = await purchaseServices.getPurchaseCart();

      return purchaseData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching landmarks
export const fetchLandmarkThunk = createAsyncThunk(
  'purchase/landmark',
  async (_, { rejectWithValue }) => {
    try {
      const landmarkData = await purchaseServices.getLandmarks();
      return landmarkData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchStoreThunk = createAsyncThunk(
  'fetch/store',
  async (_, { rejectWithValue }) => {
    try {
      const storeData = await purchaseServices.getStore();
      return storeData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching purchase cart
export const fetchOrders = createAsyncThunk(
  'customer/orders',
  async (_, { rejectWithValue }) => {
    try {
      const orderData = await purchaseServices.getOrders();
      return orderData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const webStorefrontCheckoutAsync = createAsyncThunk(
  'storefront/checkout',
  async (checkoutDetails: Checkout, { rejectWithValue }) => {
    try {
      const updatedPurchaseData =
        await purchaseServices.webStorefrontCheckout(checkoutDetails);
      return updatedPurchaseData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const addAddressDetailsAsync = createAsyncThunk(
  'storefront/addAddress',
  async (
    addressData: {
      sessionId: string;
      body: {
        address: string;
        latitude: string;
        longitude: string;
        deliveryPhoneNumber: string;
        deliveryInstructions: string;
        state: string;
        deliveryLocationId?: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const addAddressResponse =
        await purchaseServices.addAddressDetails(addressData);
      return addAddressResponse;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const webStorefrontAnonymousheckoutAsync = createAsyncThunk(
  'storefront/anonymous-checkout',
  async (checkoutDetails: Checkout, { rejectWithValue }) => {
    try {
      const updatedPurchaseData =
        await purchaseServices.webStorefrontAnonymousCheckout(checkoutDetails);
      return updatedPurchaseData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating purchase cart
export const updatePurchaseCartAsync = createAsyncThunk(
  'purchase/updatePurchaseCart',
  async (
    { CartLineItems, purchaseCode, silent = false }: CartItem,
    { rejectWithValue }
  ) => {
    try {
      const updatedPurchaseData = await purchaseServices.updatePurchaseCart({
        CartLineItems,
        purchaseCode,
      });
      return { silent, updatedPurchaseData };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating purchase cart details
export const updateOrderDetailsThunk = createAsyncThunk(
  'storefront/order-type',
  async (
    {
      orderDetails,
      succesCallback,
    }: {
      orderDetails: OrderDetails;
      succesCallback: () => void;
    },
    { rejectWithValue }
  ) => {
    try {
      const updatedPurchaseData =
        await purchaseServices.updateOrderDetails(orderDetails);
      succesCallback();
      return updatedPurchaseData;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Create the slice
const purchaseSlice = createSlice({
  name: 'purchase',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    resetCheckoutResponse: (state) => {
      state.checkoutResponse = null;
      state.anonymousCheckoutResponse = null;
    },

    copyPurchaseCart: (
      state,
      action: PayloadAction<{
        cartLineItems: CartLineItems[];
        orderDetails: OrderDetails;
      }>
    ) => {
      state.cartLineItemsCopy = action.payload.cartLineItems;
      state.orderDetailsCopy = action.payload.orderDetails;
      state.purchaseCopy = state.purchase;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchOrders cases
      .addCase(webStorefrontCheckoutAsync.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(
        webStorefrontCheckoutAsync.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.loading = false;

          if (typeof action.payload.data !== 'undefined') {
            state.checkoutResponse = action.payload.data;
          }
          state.error = null;
          // toast.success(action.payload.message)
        }
      )
      .addCase(webStorefrontCheckoutAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // toast.error(state.error)
      })

      // fetchOrders cases
      .addCase(webStorefrontAnonymousheckoutAsync.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(
        webStorefrontAnonymousheckoutAsync.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          if (typeof action.payload.data !== 'undefined') {
            state.anonymousCheckoutResponse = action.payload.data;
          }
          state.error = null;
          // toast.success(action.payload.message)
        }
      )
      .addCase(webStorefrontAnonymousheckoutAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // toast.error(state.error)
      })

      // fetchOrders cases
      .addCase(fetchOrders.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .addCase(fetchOrders.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (typeof action.payload.data !== 'undefined') {
          state.orders = action.payload.data;
        }
        state.error = null;
        // toast.success(action.payload.message)
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // toast.error(state.error)
      })
      // fetchPurchaseCart cases
      .addCase(fetchPurchaseCart.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(
        fetchPurchaseCart.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.purchase = action.payload;
          if (action.payload.miscellaneousFeeName) {
            state.miscellanousFeeName = action.payload.miscellaneousFeeName;
          }
          state.error = null;
        }
      )
      .addCase(fetchPurchaseCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // toast.error(state.error)
      })
      // updatePurchaseCartDetails cases
      .addCase(updateOrderDetailsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(updateOrderDetailsThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        toast.success('Cart updated');
      })
      .addCase(updateOrderDetailsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // toast.error(state.error)
      })
      // fetchLandmarkThunk
      .addCase(fetchLandmarkThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchLandmarkThunk.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.landmarks = action.payload;
          state.error = null;
        }
      )
      .addCase(
        fetchStoreThunk.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.store = action.payload;
          state.error = null;
        }
      )
      .addCase(fetchLandmarkThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // toast.error(state.error)
      })
      .addCase(fetchStoreThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // toast.error(state.error)
      })
      // updatePurchaseCart cases
      .addCase(updatePurchaseCartAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updatePurchaseCartAsync.fulfilled,
        (
          state,
          action: PayloadAction<{
            updatedPurchaseData: PurchaseOrder;
            silent: boolean;
          }>
        ) => {
          state.loading = false;
          state.purchase = action.payload.updatedPurchaseData;
          if (!action.payload.silent) {
            toast.success('Cart updated');
          }

          state.error = null;
        }
      )
      .addCase(updatePurchaseCartAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(
        addAddressDetailsAsync.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.store = action.payload;
          state.error = null;
          toast.success('Address Added to Order');
        }
      )
      .addCase(addAddressDetailsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Error adding addreess to order');
        // toast.error(state.error)
      });
  },
});

export const { clearError, resetCheckoutResponse, copyPurchaseCart } =
  purchaseSlice.actions;
export default purchaseSlice.reducer;
